/**
 * Enhanced TTS Service with OpenAI TTS integration and Web Speech API fallback
 */

import { supabase } from '../config/supabase';

class TTSService {
  constructor() {
    this.currentAudio = null;
    this.onSpeakingStart = null;
    this.onSpeakingEnd = null;
    this.audioCache = new Map();
    this.pendingRequests = new Map();
    this.isSpeakingActive = false;
    this.isPreloaded = false;
    this.currentAbortController = null; // Track current request to allow cancellation
    this.isPlayingLocked = false; // Global lock to prevent any audio playback overlap
    this.currentAudioId = null; // Track current audio to prevent overlapping
    
    // Web Audio API for instant playback
    this.audioContext = null;
    this.currentSource = null;
    
    // Initialize Web Audio API if available
    this.initializeWebAudio();
    
    // Clean up old cache entries first
    this.cleanupLocalStorage();
    
    // Start preloading common responses immediately
    this.preloadCommonResponses();
  }

  // Create a short hash for cache keys to avoid localStorage quota issues
  createCacheKey(text, gender, voice) {
    // Create a simple hash of the text to avoid extremely long keys
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Create a shorter, more manageable cache key
    const textHash = Math.abs(hash).toString(16);
    const textPreview = text.substring(0, 20).replace(/[^a-zA-Z0-9]/g, ''); // Clean preview
    return `${textPreview}_${textHash}_${gender}_${voice}`;
  }

  // Initialize Web Audio API for instant playback
  initializeWebAudio() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('🎵 Web Audio API initialized for instant playback');
    } catch (error) {
      console.log('🎵 Web Audio API not available, using HTML5 audio');
      this.audioContext = null;
    }
  }

  // Optimized preloading - only load from existing cache, no new API calls
  async preloadCommonResponses() {
    if (this.isPreloaded) return;
    
    // Wait a moment for system to be ready
    setTimeout(async () => {
      try {
        console.log('🚀 Optimized TTS cache loading (no API calls)...');
        
        // Only check for existing cached responses - don't generate new ones
        const potentialCachedPhrases = [
          { text: "I'm listening.", gender: 'male', voice: 'onyx' },
          { text: "I'm listening.", gender: 'female', voice: 'nova' },
          { text: "Mm-hmm.", gender: 'male', voice: 'onyx' },
          { text: "Mm-hmm.", gender: 'female', voice: 'nova' }
        ];
        
        let cachedCount = 0;
        
        // Only load from existing cache - NO API CALLS
        for (const phrase of potentialCachedPhrases) {
          const cacheKey = this.createCacheKey(phrase.text, phrase.gender, phrase.voice);
          
          // Check memory cache
          if (this.audioCache.has(cacheKey)) {
            cachedCount++;
            continue;
          }
          
          // Check localStorage cache only
          const cachedData = this.loadFromLocalStorage(cacheKey);
          if (cachedData) {
            this.audioCache.set(cacheKey, cachedData);
            cachedCount++;
            console.log(`📦 Loaded from storage: ${phrase.text}`);
          }
          // Note: We don't generate missing phrases anymore - let them be generated on-demand
        }
        
        this.isPreloaded = true;
        console.log(`✅ TTS cache loaded! Found ${cachedCount} existing cached items (0 API calls)`);
        
      } catch (error) {
        console.log('Cache loading skipped:', error.message);
        this.isPreloaded = true;
      }
    }, 1000); // Shorter wait since we're not making API calls
  }

  // Save audio blob to localStorage for persistent caching
  saveToLocalStorage(cacheKey, audioBlob) {
    try {
      // Check if we have enough space before attempting to save
      const estimatedSize = audioBlob.size * 1.5; // Base64 encoding increases size by ~33%
      
      // Try to estimate localStorage usage
      let currentUsage = 0;
      try {
        for (let key in localStorage) {
          if (localStorage.hasOwnProperty(key)) {
            currentUsage += localStorage[key].length;
          }
        }
      } catch (e) {
        // If we can't calculate usage, assume it's high
        currentUsage = 4 * 1024 * 1024; // Assume 4MB usage
      }
      
      // Skip saving if it would likely exceed quota (most browsers have ~5-10MB limit)
      const quotaLimit = 4 * 1024 * 1024; // Conservative 4MB limit
      if (currentUsage + estimatedSize > quotaLimit) {
        console.log(`💾 Skipping localStorage save - would exceed quota (${Math.round(estimatedSize/1024)}KB)`);
        return;
      }
      
      // Convert blob to base64 for localStorage
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const base64Data = reader.result;
          const storageKey = `tts_cache_${cacheKey}`;
          const cacheData = {
            data: base64Data,
            timestamp: Date.now(),
            version: '1.0' // For cache invalidation if needed
          };
          
          localStorage.setItem(storageKey, JSON.stringify(cacheData));
          console.log(`💾 Saved to localStorage: ${cacheKey.substring(0, 30)}...`);
        } catch (quotaError) {
          if (quotaError.name === 'QuotaExceededError') {
            console.log('💾 localStorage quota exceeded - clearing old cache and skipping save');
            this.cleanupLocalStorageAggressive();
          } else {
            console.log('💾 Failed to save to localStorage:', quotaError.message);
          }
        }
      };
      
      reader.onerror = () => {
        console.log('💾 Failed to read blob for localStorage');
      };
      
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.log('💾 Failed to save to localStorage:', error.message);
    }
  }

  // Load audio blob from localStorage with security validation
  loadFromLocalStorage(cacheKey) {
    try {
      const storageKey = `tts_cache_${cacheKey}`;
      const cachedItem = localStorage.getItem(storageKey);
      
      if (!cachedItem) return null;
      
      // Security: Validate JSON structure
      let cacheData;
      try {
        cacheData = JSON.parse(cachedItem);
      } catch (parseError) {
        console.warn('Invalid cache data format, removing:', storageKey);
        localStorage.removeItem(storageKey);
        return null;
      }
      
      // Security: Validate required fields
      if (!cacheData.data || !cacheData.timestamp || !cacheData.version) {
        console.warn('Invalid cache data structure, removing:', storageKey);
        localStorage.removeItem(storageKey);
        return null;
      }
      
      // Check if cache is too old (e.g., older than 7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      if (Date.now() - cacheData.timestamp > maxAge) {
        localStorage.removeItem(storageKey);
        return null;
      }
      
      // Security: Validate base64 format
      const base64Data = cacheData.data;
      if (!base64Data.startsWith('data:')) {
        console.warn('Invalid base64 format, removing:', storageKey);
        localStorage.removeItem(storageKey);
        return null;
      }
      
      // Convert base64 back to blob
      try {
        const byteCharacters = atob(base64Data.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });
        
        return audioBlob;
      } catch (conversionError) {
        console.warn('Failed to convert cached data, removing:', storageKey);
        localStorage.removeItem(storageKey);
        return null;
      }
      
    } catch (error) {
      console.log('Failed to load from localStorage:', error);
      return null;
    }
  }

  // Clear old cache entries to prevent storage bloat
  cleanupLocalStorage() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('tts_cache_')) {
          const item = localStorage.getItem(key);
          if (item) {
            const cacheData = JSON.parse(item);
            const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
            if (Date.now() - cacheData.timestamp > maxAge) {
              keysToRemove.push(key);
            }
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleaned up ${keysToRemove.length} old TTS cache entries`);
      }
    } catch (error) {
      console.log('Cache cleanup failed:', error);
    }
  }

  // Aggressive cleanup when quota is exceeded
  cleanupLocalStorageAggressive() {
    try {
      console.log('🧹 Aggressive localStorage cleanup - removing all TTS cache');
      const keysToRemove = [];
      
      // Find all TTS cache keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('tts_cache_')) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all TTS cache entries
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Continue even if individual removal fails
        }
      });
      
      console.log(`🧹 Aggressively cleaned ${keysToRemove.length} TTS cache entries`);
      
      // Also clear memory cache to be safe
      this.audioCache.clear();
      
    } catch (error) {
      console.log('🧹 Aggressive cleanup failed:', error.message);
      // As a last resort, try to clear all localStorage
      try {
        localStorage.clear();
        console.log('🧹 Cleared entire localStorage as last resort');
      } catch (clearError) {
        console.log('🧹 Could not clear localStorage:', clearError.message);
      }
    }
  }

  // Clear all caches and reset state
  clearCache() {
    this.stop();
    this.audioCache.clear();
    this.pendingRequests.clear();
    this.isSpeakingActive = false;
    if (this.onSpeakingEnd) {
      this.onSpeakingEnd();
    }
    console.log('🧹 TTS service reset');
  }

  // Clear stuck states that might prevent TTS from working
  clearStuckStates() {
    console.log('🔧 Clearing stuck TTS states...');
    
    // Clear any stuck pending requests
    this.pendingRequests.clear();
    
    // Reset speaking state if stuck
    if (this.isSpeakingActive) {
      this.isSpeakingActive = false;
      if (this.onSpeakingEnd) {
        this.onSpeakingEnd();
      }
    }
    
    // Stop any current audio
    this.stop();
    
    console.log('✅ TTS stuck states cleared');
  }

  // Performance monitoring callbacks for UI feedback
  onLoadingStart = null;
  onLoadingProgress = null;
  onLoadingComplete = null;

  // Method to set performance callbacks for UI feedback
  setPerformanceCallbacks({ onLoadingStart, onLoadingProgress, onLoadingComplete }) {
    this.onLoadingStart = onLoadingStart;
    this.onLoadingProgress = onLoadingProgress;
    this.onLoadingComplete = onLoadingComplete;
  }

  // Provide performance feedback during TTS generation
  notifyPerformanceState(state, data = {}) {
    try {
      switch(state) {
        case 'start':
          if (this.onLoadingStart) {
            this.onLoadingStart({ 
              timestamp: Date.now(),
              textLength: data.textLength,
              voice: data.voice,
              cached: data.cached || false
            });
          }
          break;
        case 'progress':
          if (this.onLoadingProgress) {
            this.onLoadingProgress({
              stage: data.stage, // 'auth', 'request', 'processing', 'caching'
              elapsed: data.elapsed,
              estimated: data.estimated
            });
          }
          break;
        case 'complete':
          if (this.onLoadingComplete) {
            this.onLoadingComplete({
              totalTime: data.totalTime,
              cached: data.cached || false,
              success: data.success || true
            });
          }
          break;
      }
    } catch (error) {
      console.warn('Performance callback error:', error.message);
    }
  }

  // Helper function to get auth token
  async getAuthToken() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }
    return session.access_token;
  }

  // Set callbacks for speaking state changes
  setCallbacks(onStart, onEnd) {
    this.onSpeakingStart = onStart;
    this.onSpeakingEnd = onEnd;
  }

  // Security: Sanitize and validate input text
  sanitizeText(text) {
    if (typeof text !== 'string') {
      throw new Error('Text must be a string');
    }
    
    // Remove potential script tags and limit length
    const sanitized = text
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .trim();
    
    // Limit text length for security and performance
    if (sanitized.length > 2000) {
      throw new Error('Text too long. Maximum 2000 characters allowed.');
    }
    
    if (sanitized.length === 0) {
      throw new Error('Text cannot be empty after sanitization');
    }
    
    return sanitized;
  }

  // Ultra-fast sanitization (optimized for performance)
  fastSanitizeText(text) {
    if (typeof text !== 'string') return String(text);
    
    // Quick length check
    if (text.length > 2000) {
      text = text.substring(0, 2000);
    }
    
    // Fast HTML removal (only if needed)
    if (text.includes('<')) {
      text = text.replace(/<[^>]*>/g, '');
    }
    
    return text.trim() || 'Hello';
  }

  // Security: Rate limiting to prevent abuse
  checkRateLimit() {
    const now = Date.now();
    const rateLimitKey = 'tts_rate_limit';
    const rateLimitWindow = 60000; // 1 minute
    const maxRequests = 30; // Max 30 requests per minute
    
    try {
      const rateLimitData = localStorage.getItem(rateLimitKey);
      let requests = [];
      
      if (rateLimitData) {
        requests = JSON.parse(rateLimitData).filter(timestamp => 
          now - timestamp < rateLimitWindow
        );
      }
      
      if (requests.length >= maxRequests) {
        throw new Error('Rate limit exceeded. Please wait before making more requests.');
      }
      
      requests.push(now);
      localStorage.setItem(rateLimitKey, JSON.stringify(requests));
      
    } catch (error) {
      console.warn('Rate limiting check failed:', error.message);
      // Continue execution - don't block on rate limit storage issues
    }
  }

  async speakText(text, options = {}) {
    const { gender = 'female', voiceId = null } = options;
    const startTime = Date.now();
    
    try {
      // PREVENT OVERLAPPING: Stop any currently playing audio first
      if (this.isSpeakingActive || this.isPlayingLocked) {
        console.log('🛑 Stopping previous TTS before starting new one');
        this.stop();
        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // INSTANT START: Set speaking state but don't trigger callback until audio actually starts
      this.isSpeakingActive = true;
      
      // FAST PATH: Quick security check (optimized for speed)
      const sanitizedText = this.fastSanitizeText(text);
      
      console.log('⚡ ULTRA-FAST TTS speakText started:', { 
        text: sanitizedText.substring(0, 50), 
        gender, 
        voiceId,
        timestamp: startTime
      });

      // Cancel any ongoing TTS request immediately
      if (this.currentAbortController) {
        this.currentAbortController.abort();
        this.currentAbortController = null;
      }

      // Stop any currently playing audio instantly
      this.stop();

      // ULTRA PRIORITY: Try instant cache first (even before security checks)
      const cacheResult = await this.tryUltraInstantCache(sanitizedText, gender, voiceId);
      if (cacheResult) {
        const cacheTime = Date.now() - startTime;
        console.log(`⚡ ULTRA INSTANT CACHE HIT: ${cacheTime}ms`);
        return cacheResult;
      }

      // OPTIMIZED PATH: Use best OpenAI TTS method with aggressive timeout
      try {
        console.log('🎤 Attempting optimized OpenAI TTS with voice:', voiceId, 'gender:', gender);
        const ttsResult = await this.speakWithOpenAIOptimized(sanitizedText, gender, voiceId);
        const totalTime = Date.now() - startTime;
        console.log(`🎤 OPTIMIZED OpenAI TTS COMPLETE: ${totalTime}ms`);
        return ttsResult;
      } catch (error) {
        console.error('🚨 Optimized OpenAI TTS failed:', error.message);
        console.log('🔄 Trying standard OpenAI method as backup...');
        try {
          // Try the regular OpenAI method as backup
          return await this.speakWithOpenAI(sanitizedText, gender, voiceId);
        } catch (backupError) {
          console.error('🚨 All OpenAI TTS methods failed:', backupError.message);
          console.log('🎙️ Final fallback to Web Speech API...');
          // INSTANT FALLBACK: Web Speech API for immediate response
          return await this.speakWithWebSpeechAPI(sanitizedText, gender, voiceId);
        }
      }
    } catch (securityError) {
      console.warn('🚨 Security check failed, using fallback:', securityError.message);
      // Even security failures get instant fallback
      try {
        return await this.speakWithWebSpeechAPI(text, gender, voiceId);
      } catch (fallbackError) {
        this.isSpeakingActive = false;
        if (this.onSpeakingEnd) {
          this.onSpeakingEnd();
        }
        throw securityError;
      }
    }
  }

  // Specialized method for preview audio - optimized for quick playback
  async speakPreviewText(text, options = {}) {
    const { gender = 'female', voiceId = 'nova' } = options;
    
    try {
      console.log('🎵 Starting preview audio playback:', { 
        text: text.substring(0, 50), 
        gender, 
        voiceId 
      });

      // Use the main speakText method but with preview-specific options
      return await this.speakText(text, { gender, voiceId });
      
    } catch (error) {
      console.error('🚨 Preview audio failed:', error);
      throw error;
    }
  }

  // Ultra-instant cache lookup - prioritize fresh AI responses over generic preloaded phrases
  async tryUltraInstantCache(text, gender, voiceId) {
    const openAIVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
    let selectedVoice = voiceId;
    if (!openAIVoices.includes(voiceId)) {
      selectedVoice = gender === 'female' ? 'nova' : 'onyx';
    }
    
    const cacheKey = this.createCacheKey(text, gender, selectedVoice);
    
    // Check if this is a generic preloaded phrase - if so, skip cache to force fresh generation
    const genericPhrases = [
      "I understand how you're feeling",
      "Tell me more about that", 
      "That makes complete sense",
      "I'm here to support you",
      "How does that make you feel",
      "Mm-hmm",
      "I'm listening"
    ];
    
    const isGenericPhrase = genericPhrases.some(phrase => 
      text.toLowerCase().includes(phrase.toLowerCase()) || 
      phrase.toLowerCase().includes(text.toLowerCase())
    );
    
    if (isGenericPhrase) {
      console.log('🚫 Skipping cache for generic phrase to ensure fresh AI response:', text.substring(0, 30));
      return null; // Force fresh generation for generic responses
    }
    
    // ANTI-REPETITION: Skip cache for responses that start with common phrases but are longer
    // This prevents the same cached audio from playing for different responses
    const commonStartPhrases = [
      "It sounds like you're feeling",
      "I can see that you're",
      "That's a really important",
      "What you're describing",
      "I hear that you're",
      "It makes sense that",
      "I understand that"
    ];
    
    const startsWithCommonPhrase = commonStartPhrases.some(phrase => 
      text.toLowerCase().startsWith(phrase.toLowerCase())
    );
    
    // If it starts with a common phrase and is longer than 100 characters, skip cache
    // This ensures fresh generation for full therapeutic responses
    if (startsWithCommonPhrase && text.length > 100) {
      console.log('🚫 Skipping cache for therapeutic response to ensure uniqueness:', text.substring(0, 50) + '...');
      return null;
    }
    
    // FASTEST: Check memory cache first (no await needed)
    const memoryCache = this.audioCache.get(cacheKey);
    if (memoryCache) {
      console.log('⚡ MEMORY CACHE HIT - fast HTML5 audio playback');
      return this.playAudioFast(memoryCache);
    }
    
    // FAST: Check localStorage cache
    const cachedData = this.loadFromLocalStorage(cacheKey);
    if (cachedData) {
      console.log('⚡ LOCALSTORAGE CACHE HIT - loading to memory with fast playback');
      this.audioCache.set(cacheKey, cachedData);
      return this.playAudioFast(cachedData);
    }
    
    return null;
  }

  async speakWithOpenAIOptimized(text, gender, voiceId) {
    const startTime = Date.now();
    
    // OPTIMIZATION: Split long text for faster first audio response
    if (text.length > 800) {
      console.log('⚡ Text chunking for faster response - length:', text.length);
      return await this.speakWithChunking(text, gender, voiceId);
    }
    
    // Voice validation for OpenAI standard voices with GENDER ENFORCEMENT
    const openAIVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
    const femaleVoices = ['nova', 'shimmer', 'alloy'];
    const maleVoices = ['onyx', 'echo', 'fable'];
    
    let selectedVoice = voiceId;
    
    // CRITICAL: Enforce gender-voice matching to prevent avatar/voice mismatch
    if (!openAIVoices.includes(voiceId)) {
      selectedVoice = gender === 'female' ? 'nova' : 'onyx';
      console.log(`🔄 Invalid voice "${voiceId}", using OpenAI default: ${selectedVoice}`);
    } else if (gender === 'female' && !femaleVoices.includes(voiceId)) {
      selectedVoice = 'nova';
      console.log(`🚨 GENDER MISMATCH FIX: Female avatar with male voice "${voiceId}" → corrected to "nova"`);
    } else if (gender === 'male' && !maleVoices.includes(voiceId)) {
      selectedVoice = 'onyx';
      console.log(`🚨 GENDER MISMATCH FIX: Male avatar with female voice "${voiceId}" → corrected to "onyx"`);
    }

    const cacheKey = this.createCacheKey(text, gender, selectedVoice);
    
    // Notify UI of TTS generation start
    this.notifyPerformanceState('start', {
      textLength: text.length,
      voice: selectedVoice,
      cached: false
    });
    
    console.log('🚀 SECURE TTS starting:', { 
      voice: selectedVoice, 
      gender,
      textLength: text.length,
      timestamp: new Date().toISOString(),
      securityMode: 'backend-only'
    });
    
    // Check cache one more time (in case it was just added)
    if (this.audioCache.has(cacheKey)) {
      console.log('⚡ Cache hit during optimized request');
      this.notifyPerformanceState('complete', {
        totalTime: Date.now() - startTime,
        cached: true,
        success: true
      });
      return this.playAudioFast(this.audioCache.get(cacheKey));
    }

    // Check if already generating
    if (this.pendingRequests.has(cacheKey)) {
      console.log('🔄 Request already in progress, waiting...');
      return await this.pendingRequests.get(cacheKey);
    }

    // SECURITY: All TTS requests go through backend to protect API keys

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const requestUrl = `${backendUrl}/api/openai/tts`;
    
    // Optimized request with aggressive timeout and parallel processing
    const requestPromise = (async () => {
      let timeoutId;
      try {
        // Get auth token quickly in parallel with other setup
        const authPromise = this.getAuthToken();
        const authStart = Date.now();
        
        const requestBody = {
          text,
          gender,
          voiceId: selectedVoice,
          model: 'tts-1', // Use fastest model for better response time
          response_format: 'mp3',
          speed: 1.2, // Slightly faster for perceived responsiveness
          optimize_streaming: true // Request streaming optimization on backend
        };
        
        // Ultra-aggressive timeout with better UX messaging
        this.currentAbortController = new AbortController();
        timeoutId = setTimeout(() => {
          console.log('⚡ Fast timeout after 10 seconds - retrying with cache fallback');
          if (this.currentAbortController) {
            this.currentAbortController.abort();
          }
        }, 10000); // Reasonable 10-second timeout

        const authToken = await authPromise;
        const authTime = Date.now() - authStart;
        
        // Notify progress after auth
        this.notifyPerformanceState('progress', {
          stage: 'auth',
          elapsed: authTime,
          estimated: authTime + 5000 // Estimate 5 more seconds
        });

        console.log(`🚀 SECURE TTS request: ${requestUrl} (Backend-only for security)`);
        const fetchStart = Date.now();
        
        const response = await fetch(requestUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'Accept': 'audio/mpeg',
            'Connection': 'keep-alive'
          },
          body: JSON.stringify(requestBody),
          signal: this.currentAbortController.signal
        });

        clearTimeout(timeoutId);
        this.currentAbortController = null;
        
        const fetchTime = Date.now() - fetchStart;
        console.log(`⚡ ULTRA-FAST fetch completed: Auth=${authTime}ms, Fetch=${fetchTime}ms, Status=${response.status}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`🚨 ULTRA-FAST TTS failed: ${response.status} ${response.statusText}`, errorText);
          throw new Error(`Ultra-fast TTS failed: ${response.status} - ${errorText}`);
        }

        const blobStart = Date.now();
        const audioBlob = await response.blob();
        const blobTime = Date.now() - blobStart;
        const totalTime = Date.now() - startTime;
        
        console.log(`🎤 SECURE TTS COMPLETE: Auth=${authTime}ms, Fetch=${fetchTime}ms, Blob=${blobTime}ms, Total=${totalTime}ms`);
        
        // Notify completion with detailed metrics
        this.notifyPerformanceState('complete', {
          totalTime,
          cached: false,
          success: true
        });
        
        // Enhanced caching for better performance
        this.audioCache.set(cacheKey, audioBlob);
        
        // Smart cache management - keep frequently used items
        if (this.audioCache.size > 12) { // Increased cache size for better hit rate
          const firstKey = this.audioCache.keys().next().value;
          this.audioCache.delete(firstKey);
          console.log('🧹 Cache cleanup - maintaining optimal size');
        }
        
        // Background localStorage save with error handling
        setTimeout(() => {
          try {
            this.saveToLocalStorage(cacheKey, audioBlob);
          } catch (e) {
            console.warn('📦 LocalStorage save failed (quota exceeded):', e.message);
          }
        }, 50); // Faster background save
        
        // Enhanced playback with preloading
        return this.playAudioFast(audioBlob);
        
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          console.log('🚨 Optimized request aborted due to timeout (15 seconds)');
        } else {
          console.error('🚨 Optimized request failed:', error.message);
        }
        throw error;
      } finally {
        this.pendingRequests.delete(cacheKey);
        if (this.currentAbortController) {
          this.currentAbortController = null;
        }
      }
    })();

    this.pendingRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  }

  // ULTRA-FAST: Direct OpenAI API method (bypasses backend for maximum speed)
  // OPTIMIZATION: Chunked TTS for long text - start playing first chunk while generating others
  async speakWithChunking(text, gender, voiceId) {
    console.log('⚡ CHUNKED TTS starting for better perceived speed');
    
    // Smart text chunking at sentence boundaries
    const chunks = this.smartChunkText(text, 400); // Smaller chunks for faster first response
    console.log(`⚡ Split into ${chunks.length} chunks:`, chunks.map(c => c.length));
    
    if (chunks.length === 1) {
      // If no chunking needed, use regular method
      return await this.speakWithOpenAIOptimized(chunks[0], gender, voiceId);
    }
    
    // Start first chunk immediately for best UX
    const firstChunkPromise = this.speakWithOpenAIOptimized(chunks[0], gender, voiceId);
    
    // Generate remaining chunks in parallel for better performance
    const remainingChunkPromises = chunks.slice(1).map(chunk => 
      this.generateAudioChunk(chunk, gender, voiceId)
    );
    
    try {
      // Play first chunk immediately
      await firstChunkPromise;
      
      // Play remaining chunks as they become available
      for (let i = 0; i < remainingChunkPromises.length; i++) {
        const audioBlob = await remainingChunkPromises[i];
        await this.playAudioFast(audioBlob);
      }
      
      console.log('⚡ CHUNKED TTS completed successfully');
    } catch (error) {
      console.error('❌ Chunked TTS failed:', error);
      throw error;
    }
  }

  // Helper: Generate audio for a chunk without playing
  async generateAudioChunk(text, gender, voiceId) {
    const cacheKey = this.createCacheKey(text, gender, voiceId);
    
    // Check cache first
    if (this.audioCache.has(cacheKey)) {
      return this.audioCache.get(cacheKey);
    }
    
    // SECURITY: All requests go through backend to protect API keys
    const authToken = await this.getAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    
    const response = await fetch(`${backendUrl}/api/openai/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        text,
        gender,
        voiceId,
        model: 'tts-1',
        response_format: 'mp3'
      })
    });
    
    if (response.ok) {
      const audioBlob = await response.blob();
      this.audioCache.set(cacheKey, audioBlob);
      return audioBlob;
    }
    
    throw new Error('Failed to generate audio chunk');
  }

  // Helper: Smart text chunking at sentence boundaries
  smartChunkText(text, maxChunkSize = 400) {
    if (text.length <= maxChunkSize) {
      return [text];
    }
    
    const chunks = [];
    let currentChunk = '';
    
    // Split by sentences
    const sentences = text.split(/(?<=[.!?])\s+/);
    
    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length <= maxChunkSize) {
        currentChunk += (currentChunk ? ' ' : '') + sentence;
      } else {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
          currentChunk = sentence;
        } else {
          // Sentence too long, split by commas or words
          if (sentence.length > maxChunkSize) {
            const words = sentence.split(' ');
            for (const word of words) {
              if (currentChunk.length + word.length + 1 <= maxChunkSize) {
                currentChunk += (currentChunk ? ' ' : '') + word;
              } else {
                if (currentChunk) {
                  chunks.push(currentChunk.trim());
                }
                currentChunk = word;
              }
            }
          } else {
            currentChunk = sentence;
          }
        }
      }
    }
    
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks.filter(chunk => chunk.length > 0);
  }

  // Helper method to unlock playback and clean up state
  unlockPlayback(audioId) {
    // Only unlock if this is the current audio
    if (this.currentAudioId === audioId) {
      console.log(`🔓 Unlocking audio playback for ID: ${audioId}`);
      this.isPlayingLocked = false;
      this.currentAudioId = null;
      this.currentAudio = null;
      this.isSpeakingActive = false;
      
      if (this.onSpeakingEnd) {
        this.onSpeakingEnd();
      }
    } else {
      console.log(`🔒 Not unlocking - audio ID mismatch: ${audioId} vs ${this.currentAudioId}`);
    }
  }

  // Fast audio playback optimized for OpenAI TTS
  playAudioFast(audioBlob) {
    return new Promise((resolve, reject) => {
      console.log('⚡ ULTRA-FAST playback starting - blob size:', audioBlob.size);
      
      // GLOBAL LOCK: Prevent any new audio if something is already playing
      if (this.isPlayingLocked) {
        console.log('🔒 Audio playback locked - rejecting new request');
        reject(new Error('Audio playback is locked'));
        return;
      }
      
      // Set global lock immediately
      this.isPlayingLocked = true;
      const audioId = Date.now() + Math.random();
      this.currentAudioId = audioId;
      
      const playStart = Date.now();
      const audioUrl = URL.createObjectURL(audioBlob);
      this.currentAudio = new Audio(audioUrl);
      
      console.log(`⚡ ULTRA-FAST audio lock acquired for ID: ${audioId}`);
      
      // Reduced minimum loading time for faster UX
      const minLoadingTime = 200; // Reduced to 200ms for faster response
      let hasPlayedAudio = false;
      
      // Ultra-optimized settings for instant start
      this.currentAudio.preload = 'auto';
      this.currentAudio.volume = 1.0;
      this.currentAudio.playbackRate = 1.0; // Ensure normal speed
      
      // IMMEDIATE playback attempt - no waiting for canplay if possible
      const attemptImmediatePlay = async () => {
        try {
          // Try to play immediately without waiting
          const playPromise = this.currentAudio.play();
          
          if (playPromise) {
            await playPromise;
            hasPlayedAudio = true;
            const startTime = Date.now() - playStart;
            console.log(`⚡ INSTANT audio start in ${startTime}ms`);
            
            if (this.onSpeakingStart && this.currentAudioId === audioId) {
              this.onSpeakingStart();
            }
          }
        } catch (error) {
          // If immediate play fails, fall back to canplay event
          console.log('⚡ Immediate play failed, using canplay fallback');
          this.currentAudio.addEventListener('canplay', startPlayback, { once: true });
        }
      };
      
      // Optimized playback start with minimal delay
      const startPlayback = async () => {
        // Double-check lock and ID
        if (!this.isPlayingLocked || this.currentAudioId !== audioId || hasPlayedAudio) {
          console.log('🔒 Lock lost or already played - aborting');
          return;
        }
        
        // Minimal loading time for better UX
        const elapsed = Date.now() - playStart;
        if (elapsed < minLoadingTime) {
          const remainingTime = minLoadingTime - elapsed;
          console.log(`⏱️ Brief loading delay: ${remainingTime}ms`);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        // Double-check again after minimal delay
        if (!this.isPlayingLocked || this.currentAudioId !== audioId || hasPlayedAudio) {
          console.log('🔒 Lock lost during delay - aborting');
          return;
        }
        
        hasPlayedAudio = true;
        console.log('⚡ Starting optimized audio playback...');
        
        this.currentAudio.play()
          .then(() => {
            const startTime = Date.now() - playStart;
            console.log(`✅ ULTRA-FAST audio started in ${startTime}ms`);
            
            // Only call onSpeakingStart after successful play
            if (this.onSpeakingStart && this.currentAudioId === audioId) {
              this.onSpeakingStart();
            }
          })
          .catch(error => {
            console.error('❌ Audio play failed:', error);
            this.unlockPlayback(audioId);
            reject(error);
          });
      };

      // Try immediate play first for maximum speed
      attemptImmediatePlay();
      
      // Handle successful completion
      this.currentAudio.addEventListener('ended', () => {
        const totalTime = Date.now() - playStart;
        console.log(`⚡ ULTRA-FAST audio completed in ${totalTime}ms`);
        URL.revokeObjectURL(audioUrl);
        this.unlockPlayback(audioId);
        resolve();
      });

      // Handle errors
      this.currentAudio.addEventListener('error', (error) => {
        console.error('❌ Audio error:', error);
        URL.revokeObjectURL(audioUrl);
        this.unlockPlayback(audioId);
        reject(error);
      });
      
      // Start loading
      this.currentAudio.load();
    });
  }

  // Ultra-instant audio playback using Web Audio API
  async playAudioWebAudio(audioBlob) {
    if (!this.audioContext) {
      // Fallback to regular HTML5 audio
      return this.playAudioInstant(audioBlob);
    }

    return new Promise(async (resolve, reject) => {
      const playStart = Date.now();
      console.log('🎵 playAudioWebAudio starting with Web Audio API');
      
      try {
        // Resume audio context if suspended (required by some browsers)
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        // Convert blob to array buffer
        const arrayBuffer = await audioBlob.arrayBuffer();
        const setupTime = Date.now() - playStart;
        console.log(`🎵 Array buffer ready in ${setupTime}ms`);

        // Decode audio data
        const decodeStart = Date.now();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        const decodeTime = Date.now() - decodeStart;
        console.log(`🎵 Audio decoded in ${decodeTime}ms`);

        // Stop any currently playing audio
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource = null;
        }

        // Create and configure audio source
        this.currentSource = this.audioContext.createBufferSource();
        this.currentSource.buffer = audioBuffer;
        this.currentSource.connect(this.audioContext.destination);

        // Set up event handlers
        this.currentSource.onended = () => {
          const totalTime = Date.now() - playStart;
          console.log(`🎵 Web Audio playback completed in ${totalTime}ms`);
          this.currentSource = null;
          this.isSpeakingActive = false;
          if (this.onSpeakingEnd) {
            this.onSpeakingEnd();
          }
          resolve();
        };

        // Start playback immediately
        const playTime = Date.now() - playStart;
        console.log(`🎵 Starting Web Audio playback after ${playTime}ms setup`);
        this.currentSource.start(0);
        
      } catch (error) {
        console.error('🎵 Web Audio playback failed:', error);
        this.currentSource = null;
        this.isSpeakingActive = false;
        if (this.onSpeakingEnd) {
          this.onSpeakingEnd();
        }
        // Fallback to regular audio
        try {
          return this.playAudioInstant(audioBlob);
        } catch (fallbackError) {
          reject(error);
        }
      }
    });
  }

  // Web Speech API fallback for instant response
  async speakWithWebSpeechAPI(text, gender, voiceId) {
    console.log('🎙️ Using Web Speech API fallback for instant response');
    
    return new Promise((resolve) => {
      if (!window.speechSynthesis) {
        throw new Error('Speech synthesis not supported');
      }

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Optimize for speed and quality
      utterance.rate = 1.25; // Faster, more upbeat pace (matching OpenAI TTS)
      utterance.pitch = gender === 'male' ? 0.8 : 1.1;
      utterance.volume = 1.0;
      
      // Try to use a good voice
      const voices = speechSynthesis.getVoices();
      const preferredVoices = gender === 'male' 
        ? ['Google UK English Male', 'Microsoft David', 'Alex']
        : ['Google UK English Female', 'Microsoft Zira', 'Samantha'];
        
      for (const voiceName of preferredVoices) {
        const voice = voices.find(v => v.name.includes(voiceName.split(' ')[0]));
        if (voice) {
          utterance.voice = voice;
          break;
        }
      }
      
      utterance.onend = () => {
        console.log('🎙️ Web Speech API ended');
        this.isSpeakingActive = false;
        if (this.onSpeakingEnd) {
          this.onSpeakingEnd();
        }
        resolve();
      };
      
      utterance.onerror = () => {
        console.log('🎙️ Web Speech API error - ending gracefully');
        this.isSpeakingActive = false;
        if (this.onSpeakingEnd) {
          this.onSpeakingEnd();
        }
        resolve();
      };
      
      console.log('🎙️ Starting Web Speech API playback');
      speechSynthesis.speak(utterance);
    });
  }

  async speakWithOpenAI(text, gender, voiceId) {
    // Only use OpenAI standard voices - no legacy mappings
    const OPENAI_STANDARD_VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
    const FEMALE_VOICES = ['nova', 'shimmer', 'alloy'];
    const MALE_VOICES = ['onyx', 'echo', 'fable'];
    
    let selectedVoice = voiceId;
    
    // CRITICAL: Enforce gender-voice matching to prevent avatar/voice mismatch
    if (!OPENAI_STANDARD_VOICES.includes(voiceId)) {
      selectedVoice = gender === 'female' ? 'nova' : 'onyx';
      console.log(`🔄 Invalid voice "${voiceId}", using default: ${selectedVoice}`);
    } else if (gender === 'female' && !FEMALE_VOICES.includes(voiceId)) {
      selectedVoice = 'nova';
      console.log(`🚨 GENDER MISMATCH FIX: Female avatar with male voice "${voiceId}" → corrected to "nova"`);
    } else if (gender === 'male' && !MALE_VOICES.includes(voiceId)) {
      selectedVoice = 'onyx';
      console.log(`🚨 GENDER MISMATCH FIX: Male avatar with female voice "${voiceId}" → corrected to "onyx"`);
    }

    const cacheKey = this.createCacheKey(text, gender, selectedVoice);
    
    console.log('🎤 TTS Debug - OpenAI Standard Voice:', { 
      originalVoiceId: voiceId, 
      selectedVoice, 
      gender,
      textLength: text.length 
    });
    
    // Check cache first
    if (this.audioCache.has(cacheKey)) {
      console.log('🎵 Using cached audio');
      return this.playAudioBlob(this.audioCache.get(cacheKey));
    }

    console.log('🎤 Making TTS request to backend with OpenAI voice:', selectedVoice);
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const requestUrl = `${backendUrl}/api/personalized-plan/text-to-speech`;
    
    try {
      const authToken = await this.getAuthToken();
      console.log('🔑 Auth token obtained, making request to:', requestUrl);

      const requestBody = {
        text,
        gender,
        voiceId: selectedVoice,
        model: 'tts-1',
        response_format: 'mp3'
      };
      
      console.log('📤 TTS Request body:', { 
        ...requestBody, 
        text: text.substring(0, 100) + (text.length > 100 ? '...' : '')
      });

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 TTS Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ TTS request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`TTS request failed: ${response.status} - ${errorText}`);
      }

      console.log('📦 Converting response to blob...');
      const audioBlob = await response.blob();
      console.log('✅ Audio blob created:', { size: audioBlob.size, type: audioBlob.type });
      
      this.audioCache.set(cacheKey, audioBlob);
      
      console.log('🎵 Starting audio playback...');
      return this.playAudioBlob(audioBlob);
      
    } catch (error) {
      console.error('❌ TTS speakWithOpenAI error:', error);
      throw error;
    }
  }

  playAudioBlob(audioBlob) {
    return new Promise((resolve, reject) => {
      console.log('🎵 playAudioBlob called with blob:', { size: audioBlob.size, type: audioBlob.type });
      
      // GLOBAL LOCK: Prevent any new audio if something is already playing
      if (this.isPlayingLocked) {
        console.log('🔒 Audio playback locked - rejecting new request');
        reject(new Error('Audio playback is locked'));
        return;
      }
      
      // Set global lock immediately
      this.isPlayingLocked = true;
      const audioId = Date.now() + Math.random();
      this.currentAudioId = audioId;
      
      const playStart = Date.now();
      const audioUrl = URL.createObjectURL(audioBlob);
      this.currentAudio = new Audio(audioUrl);
      
      console.log(`🔒 Audio lock acquired for ID: ${audioId}`);
      
      // Minimum loading time to ensure loading state is visible
      const minLoadingTime = 500; // 500ms minimum
      let hasPlayedAudio = false;
      
      // Optimize for faster playback start
      this.currentAudio.preload = 'auto';
      this.currentAudio.volume = 1.0;
      
      // Single, simple event handler
      const startPlayback = async () => {
        // Double-check lock and ID
        if (!this.isPlayingLocked || this.currentAudioId !== audioId) {
          console.log('🔒 Lock lost or audio ID changed - aborting');
          return;
        }
        
        // Ensure minimum loading time has passed
        const elapsed = Date.now() - playStart;
        if (elapsed < minLoadingTime) {
          const remainingTime = minLoadingTime - elapsed;
          console.log(`⏱️ Waiting ${remainingTime}ms to show loading state`);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        // Double-check again after delay
        if (!this.isPlayingLocked || this.currentAudioId !== audioId || hasPlayedAudio) {
          console.log('🔒 Lock lost during delay - aborting');
          return;
        }
        
        hasPlayedAudio = true;
        console.log('🎵 Starting audio playback...');
        
        this.currentAudio.play()
          .then(() => {
            console.log('✅ Audio play() succeeded');
            
            // Only call onSpeakingStart after successful play
            if (this.onSpeakingStart && this.currentAudioId === audioId) {
              this.onSpeakingStart();
            }
          })
          .catch(error => {
            console.error('❌ Audio play failed:', error);
            this.unlockPlayback(audioId);
            reject(error);
          });
      };

      // Use single event listener only
      this.currentAudio.addEventListener('canplay', startPlayback, { once: true });
      
      this.currentAudio.addEventListener('ended', () => {
        console.log('🔇 Audio playback ended');
        URL.revokeObjectURL(audioUrl);
        this.unlockPlayback(audioId);
        resolve();
      });

      this.currentAudio.addEventListener('error', (error) => {
        console.error('❌ Audio playback error:', error);
        URL.revokeObjectURL(audioUrl);
        this.unlockPlayback(audioId);
        reject(error);
      });
      
      // Start loading the audio
      console.log('🎵 Loading audio data...');
      this.currentAudio.load();
    });
  }

  // Generate audio blob without playing it (for preloading)
  async generateAudioOnly(text, gender, voiceId) {
    const startTime = Date.now();
    
    // OpenAI TTS standard voices only
    const openAIVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
    
    // Use the voice directly if it's a valid OpenAI voice, otherwise use gender defaults
    let selectedVoice = voiceId;
    if (!openAIVoices.includes(voiceId)) {
      selectedVoice = gender === 'female' ? 'nova' : 'onyx';
    }

    const mappedVoiceId = selectedVoice;
    const cacheKey = this.createCacheKey(text, gender, mappedVoiceId);
    
    console.log('🔇 Silent generation - Voice mapping:', { 
      originalVoiceId: voiceId, 
      mappedVoiceId, 
      gender,
      textLength: text.length
    });
    
    // Check cache first
    if (this.audioCache.has(cacheKey)) {
      console.log('🎵 Using cached audio for silent generation');
      return this.audioCache.get(cacheKey);
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const requestUrl = `${backendUrl}/api/openai/tts`;
    
    try {
      const authToken = await this.getAuthToken();

      const requestBody = {
        text,
        gender,
        voiceId: mappedVoiceId,
        model: 'tts-1',
        response_format: 'mp3'
      };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const totalTime = Date.now() - startTime;
      
      console.log(`🔇 Silent generation completed in ${totalTime}ms`);
      
      // Cache the result
      this.audioCache.set(cacheKey, audioBlob);
      
      return audioBlob;
      
    } catch (error) {
      console.error('🔇 Silent generation failed:', error);
      throw error;
    }
  }

  stop() {
    console.log('🛑 TTS Stop called');
    
    // Cancel any ongoing TTS requests immediately
    if (this.currentAbortController) {
      console.log('🛑 Aborting ongoing TTS request');
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }

    // Force unlock playback regardless of audio ID
    const currentId = this.currentAudioId;
    this.isPlayingLocked = false;
    this.currentAudioId = null;

    // Stop Web Audio API source immediately
    if (this.currentSource) {
      try {
        this.currentSource.stop();
        this.currentSource.disconnect();
        console.log('🛑 Web Audio source stopped');
      } catch (error) {
        console.log('🛑 Web Audio source already stopped');
      }
      this.currentSource = null;
    }

    // Stop HTML5 audio immediately
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio.removeEventListener('ended', this.handleAudioEnd);
      this.currentAudio.removeEventListener('error', this.handleAudioError);
      console.log('🛑 HTML5 audio stopped');
      this.currentAudio = null;
    }

    // Stop speech synthesis immediately
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      console.log('🛑 Speech synthesis cancelled');
    }

    // Immediately update speaking state and call callbacks
    if (this.isSpeakingActive) {
      this.isSpeakingActive = false;
      console.log('🛑 TTS speaking state cleared');
      
      if (this.onSpeakingEnd) {
        console.log('🛑 Calling onSpeakingEnd callback');
        this.onSpeakingEnd();
      }
    }
    
    console.log(`🔓 Forcefully unlocked audio (was ID: ${currentId})`);
  }

  isSpeaking() {
    return this.isSpeakingActive || (this.currentAudio && !this.currentAudio.paused);
  }
}

// Create TTS service instance
const ttsServiceInstance = new TTSService();

// Debug helper (only in development)
if (process.env.NODE_ENV === 'development') {
  window.ttsService = ttsServiceInstance;
  console.log('🛠️ TTS Service available globally as window.ttsService for debugging');
  console.log('   Use window.ttsService.clearCache() to clear audio cache');
}

export const ttsService = ttsServiceInstance;
